import type {
  NbaGameDataResponse,
  NbaPlayByPlayResponse,
  NbaStatKey,
  NbaEvent,
  NbaTeamGameModel,
  NbaBoxGameState,
  FieldGoalEvent,
} from '@statmuse/core/gamera'
import type { PlayerMedia } from '@statmuse/core/graphql'
import { last } from 'lodash-es'
import { atom, computed, map } from 'nanostores'

export const gameScore = map({ home: 0, away: 0 })

export const lineScore = atom<{
  away?: NbaGameDataResponse<NbaStatKey>['awayTeam']['lineScore']
  home?: NbaGameDataResponse<NbaStatKey>['awayTeam']['lineScore']
}>({})

export const players = map<Record<string, PlayerMedia | undefined>>({})

export const playerStats = map<{
  away?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['player']
  home?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['player']
}>({})

export const teamStats = map<{
  away?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['team']
  home?: NonNullable<NbaTeamGameModel<NbaStatKey>['stats']>['team']
}>({})

export const starters = map<{ away: number[]; home: number[] }>({
  away: [],
  home: [],
})

export const plays = atom<NbaEvent[]>([])

export const timeouts = atom({ away: 7, home: 7 })

export const inBonus = atom({ away: false, home: false })

export const quarterClock = computed(plays, ($plays) => {
  const lastPlay = last($plays.filter((p) => p.type !== 'legacyFieldGoal'))

  return {
    quarter: lastPlay?.period,
    clock: lastPlay?.type === 'endPeriod' ? '0:00' : lastPlay?.clock ?? '12:00',
    isHalftime: lastPlay?.type === 'endPeriod' && lastPlay.period === 2,
    endOfQuarter: lastPlay?.type === 'endPeriod',
  }
})

export const selectedId = atom<
  { id: number; alignment: 'home' | 'away' } | undefined
>()

export const selectedPlayer = computed(
  [selectedId, players, playerStats, plays],
  ($selectedId, $players, $stats, $plays) => {
    if (!$selectedId) return undefined
    const player = $players[$selectedId.id + '']

    const shots = $plays.filter(
      (p): p is FieldGoalEvent =>
        p.type === 'fieldGoal' && p.playerId === $selectedId.id,
    )

    return {
      player,
      shots,
      alignment: $selectedId.alignment,
    }
  },
)

export const init = (props: {
  gameData: NbaGameDataResponse<NbaStatKey>
  playByPlay?: NbaPlayByPlayResponse
  players: Record<string, PlayerMedia | undefined>
}) => {
  gameScore.set({
    away: props.gameData.awayTeam.score,
    home: props.gameData.homeTeam.score,
  })
  lineScore.set({
    away: props.gameData.awayTeam.lineScore,
    home: props.gameData.homeTeam.lineScore,
  })
  playerStats.set({
    away: props.gameData.awayTeam.stats?.player,
    home: props.gameData.homeTeam.stats?.player,
  })
  teamStats.set({
    away: props.gameData.awayTeam.stats?.team,
    home: props.gameData.homeTeam.stats?.team,
  })
  timeouts.set({
    away: props.gameData.awayTeam.remainingTimeouts ?? 7,
    home: props.gameData.homeTeam.remainingTimeouts ?? 7,
  })
  inBonus.set({
    away: props.gameData.awayTeam.inBonus ?? false,
    home: props.gameData.homeTeam.inBonus ?? false,
  })
  players.set(props.players)
  starters.set({
    away:
      props.gameData.awayTeam.players
        ?.filter((p) => p.starter)
        .map((p) => p.playerId) ?? [],
    home:
      props.gameData.homeTeam.players
        ?.filter((p) => p.starter)
        .map((p) => p.playerId) ?? [],
  })
  plays.set(props.playByPlay?.events ?? [])
}

export const update = (data: NbaBoxGameState) => {
  gameScore.set(data.gameScore)
  playerStats.set(data.playerStats)
  teamStats.set(data.teamStats)
  lineScore.set(data.lineScore)
  timeouts.set(data.remainingTimeouts)
  inBonus.set(data.inBonus)

  plays.set([
    ...plays.get().filter((e) => e.period !== data.currentQuarter),
    ...data.lastEvents,
  ])
}
